import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function isUrl(text) {
  const s = (text || "").trim();
  if (!s || /\s/.test(s)) return false;
  if (/^https?:\/\/\S+\.\S+/i.test(s)) return true;
  if (/^www\.\S+\.\S+/i.test(s)) return true;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(s);
}

export function normalizeUrl(text) {
  const s = (text || "").trim();
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

// Generic SSE consumer removed — the app uses the production-safe job+polling transport below.

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Production-safe transport: submit a job (fast), then poll a fast status endpoint.
// No single request is ever long, so it bypasses any ingress/SSE timeout.
async function runJob(kind, params, { onProgress } = {}) {
  const { data } = await axios.post(`${API}/jobs`, { kind, ...params });
  const jobId = data.job_id;
  const started = Date.now();
  const TIMEOUT_MS = 240000;

  for (;;) {
    await sleep(2200);
    let st;
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`);
      st = res.data;
    } catch {
      // transient poll failure — keep trying until the overall timeout
      if (Date.now() - started > TIMEOUT_MS) throw new Error("This is taking too long. Please try again.");
      continue;
    }
    if (st.status !== "done") onProgress?.({ phase: st.phase, engines: st.engines });
    if (st.status === "done") return st.result;
    if (st.status === "error") throw new Error(st.error || "Generation failed. Please try again.");
    if (Date.now() - started > TIMEOUT_MS) throw new Error("This is taking too long. Please try again.");
  }
}

export function createInsightStream(query, language = "auto", opts = {}) {
  const { onProgress, reasoning, attitude } = opts;
  return runJob("insight", { query, language, reasoning, attitude }, { onProgress });
}

export function createInterpretStream(url, opts = {}) {
  const { onProgress, language, reasoning, attitude } = opts;
  return runJob("interpret", { url, language, reasoning, attitude }, { onProgress });
}

export async function uploadDocument(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { file_id, filename, size, chars }
}

export function analyzeDocument(fileId, opts = {}) {
  const { onProgress, language, reasoning, attitude } = opts;
  return runJob("document", { file_id: fileId, language, reasoning, attitude }, { onProgress });
}

export function createCrossref(urls, opts = {}) {
  const { onProgress, language, reasoning, attitude } = opts;
  return runJob("crossref", { urls, language, reasoning, attitude }, { onProgress });
}

export async function getCrossref(id) {
  const { data } = await axios.get(`${API}/crossref/${id}`);
  return data;
}

export async function getChatThread(docId) {
  try {
    const { data } = await axios.get(`${API}/chat/${docId}`);
    return data.messages || [];
  } catch {
    return [];
  }
}

export async function getInsight(id) {
  const res = await axios.get(`${API}/insight/${id}`);
  return res.data;
}

export async function getInterpret(id) {
  const res = await axios.get(`${API}/interpret/${id}`);
  return res.data;
}

// ---------------- Semantic Memory (local, zero-friction) ----------------
const MEMORY_KEY = "rexxpons_memory_v1";

export function loadMemory() {
  try {
    return JSON.parse(localStorage.getItem(MEMORY_KEY) || "[]");
  } catch {
    return [];
  }
}

export function saveToMemory(entry) {
  const items = loadMemory();
  const next = [entry, ...items.filter((i) => i.id !== entry.id)].slice(0, 40);
  localStorage.setItem(MEMORY_KEY, JSON.stringify(next));
  return next;
}

export function clearMemory() {
  localStorage.removeItem(MEMORY_KEY);
}

// ---------------- Cognitive Command settings (local) ----------------
const SETTINGS_KEY = "rexxpons_settings_v1";
export const DEFAULT_SETTINGS = { language: "Auto", reasoning: "Balanced", attitude: "Neutral" };

export function loadSettings() {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  return s;
}

export async function chatWithDoc(docId, docKind, messages) {
  const { data } = await axios.post(`${API}/chat`, { doc_id: docId, doc_kind: docKind, messages });
  return data.reply;
}

export async function shareText(text) {
  if (navigator.share) {
    try {
      await navigator.share({ title: "Rexxpons Insight", text });
      return true;
    } catch (e) {
      if (e && e.name === "AbortError") return true; // user cancelled — not an error
      // otherwise fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ---------------- Translation (async job) ----------------
export function translateDoc(docId, docKind, language, opts = {}) {
  return runJob("translate", { doc_id: docId, doc_kind: docKind, language }, opts);
}

// ---------------- Multimedia ----------------
export async function getMedia(query) {
  const { data } = await axios.get(`${API}/media`, { params: { q: query } });
  return data;
}

// ---------------- User messaging ----------------
export async function sendMessage(payload) {
  const { data } = await axios.post(`${API}/messages`, payload, { withCredentials: true });
  return data;
}

// ---------------- Cognitive reference submissions ----------------
export async function submitReference(payload) {
  const { data } = await axios.post(`${API}/references`, payload, { withCredentials: true });
  return data;
}

export async function getApprovedReferences(docId) {
  try {
    const { data } = await axios.get(`${API}/references/approved`, { params: { doc_id: docId } });
    return data.references || [];
  } catch {
    return [];
  }
}

// ---------------- Admin control panel ----------------
// Admin session lives in an httpOnly cookie set by the backend.
localStorage.removeItem("rexxpons_admin_token");
export async function adminLogout() {
  try {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
  } catch (e) {
    console.error("Admin logout failed", e);
  }
}
function authHeaders() {
  return {};
}

export async function adminLogin(email, password) {
  const { data } = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
  return { email: data.email };
}
export async function adminMe() {
  const { data } = await axios.get(`${API}/admin/me`, { headers: authHeaders(), withCredentials: true });
  return data;
}
export async function getAdminStats() {
  const { data } = await axios.get(`${API}/admin/stats`, { headers: authHeaders(), withCredentials: true });
  return data;
}
export async function getAdminMessages() {
  const { data } = await axios.get(`${API}/admin/messages`, { headers: authHeaders(), withCredentials: true });
  return data;
}
export async function markMessageRead(id) {
  const { data } = await axios.post(`${API}/admin/messages/${id}/read`, {}, { headers: authHeaders(), withCredentials: true });
  return data;
}
export async function getAdminReferences() {
  const { data } = await axios.get(`${API}/admin/references`, { headers: authHeaders(), withCredentials: true });
  return data;
}
export async function moderateReference(id, status) {
  const { data } = await axios.post(`${API}/admin/references/${id}/moderate`, { status }, { headers: authHeaders(), withCredentials: true });
  return data;
}

// ---------------- Emergent Google Auth (optional per-user sign-in) ----------------
export function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export async function postAuthSession(sessionId) {
  const { data } = await axios.post(`${API}/auth/session`, {}, { headers: { "X-Session-ID": sessionId }, withCredentials: true });
  return data;
}

export async function getAuthMe() {
  const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
  return data;
}

export async function registerUser(payload) {
  const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
  return data;
}

export async function loginUser(email, password) {
  const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
  return data;
}

export async function authLogout() {
  await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
}

// ---------------- Per-user cloud Memory ----------------
export async function getUserMemory() {
  const { data } = await axios.get(`${API}/user/memory`, { withCredentials: true });
  return data.items || [];
}

export async function addUserMemory(item) {
  const { data } = await axios.post(`${API}/user/memory`, item, { withCredentials: true });
  return data.items || [];
}

export async function clearUserMemory() {
  await axios.delete(`${API}/user/memory`, { withCredentials: true });
}

// ---------------- Pricing / Stripe checkout ----------------
export async function createCheckout(lookupKey) {
  const { data } = await axios.post(
    `${API}/payments/checkout`,
    { lookup_key: lookupKey, origin_url: window.location.origin },
    { withCredentials: true }
  );
  return data;
}

export async function getPaymentStatus(sessionId) {
  const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
  return data;
}

const aeoOpts = () => ({ headers: authHeaders(), withCredentials: true });
export async function getAeoConfig() {
  const { data } = await axios.get(`${API}/admin/aeo/config`, aeoOpts());
  return data;
}
export async function saveAeoConfig(cfg) {
  const { data } = await axios.put(`${API}/admin/aeo/config`, cfg, aeoOpts());
  return data;
}
export async function detectAeoSchema(url) {
  const { data } = await axios.post(`${API}/admin/aeo/schema-detect`, { url }, aeoOpts());
  return data;
}
export async function runAeoDispatch(urls) {
  const { data } = await axios.post(`${API}/admin/aeo/dispatch`, { urls }, aeoOpts());
  return data;
}
export async function getAeoRuns() {
  const { data } = await axios.get(`${API}/admin/aeo/runs`, aeoOpts());
  return data;
}
